import { Box, Flex, Text, Button, Image } from '@chakra-ui/react'
import { Outlet, Link, useNavigate, useParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Building2, Eye, EyeOff, Globe, LogOut, User } from 'lucide-react'
import { trackEvent } from '../lib/tracking.js'
import { useAuth } from '../hooks/useAuth.js'
import { useMyOrganizations } from '../hooks/useMyOrganizations.js'
import { usePreviewMode } from '../hooks/usePreviewMode.js'
import { useProfileModal } from '../hooks/useProfileModal.js'
import { useSeedProfileName } from '../hooks/useSeedProfileName.js'
import { useGuestProgressMigration } from '../hooks/useGuestProgressMigration.js'
import { ProfileEditModal } from './ProfileEditModal.js'
import { LearnFooter } from './LearnFooter.js'
import { useLocalePath } from '../hooks/useLocalePath.js'
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../lib/constants.js'
import { persistLanguagePreference } from '../lib/language-preference.js'
import { getBrand } from '../brand/index.js'

export function LearnShell() {
  const brand = getBrand()
  const { t, i18n } = useTranslation()
  const { user, logout } = useAuth()
  const { memberships } = useMyOrganizations()
  const adminMembership = memberships.find(m => m.role === 'org_admin')
  const { isPreviewMode, isAdmin, isStudentView, toggleStudentView } = usePreviewMode()
  const { isOpen, openProfileModal, closeProfileModal } = useProfileModal()
  // Seed the certificate name from the OAuth identity (LinkedIn/Google) if the
  // profile has none yet — no-op for OTP signups and never overwrites.
  useSeedProfileName()
  // Open access: a learner can accrue progress logged-out (localStorage shadow);
  // once they log in, migrate it into the account (server re-checks access).
  useGuestProgressMigration()
  const localePath = useLocalePath()
  const navigate = useNavigate()
  const { lang } = useParams<{ lang: string }>()
  const location = useLocation()

  const changeLanguage = (newLang: string) => {
    trackEvent('learn:lang:switch', { from: lang || 'en', to: newLang })
    // Capture the choice on the user's account (best-effort) so lifecycle emails
    // match the language they actually browse in. No-op when anonymous.
    void persistLanguagePreference(newLang as SupportedLanguage)
    const newPath = location.pathname.replace(`/${lang}`, `/${newLang}`)
    navigate(newPath)
  }

  return (
    <Flex direction="column" minH="100vh">
      {/* Admin banner: orange for preview mode, blue for student view */}
      {isAdmin && (
        <Box
          bg={isStudentView ? 'blue.100' : 'orange.100'}
          textAlign="center"
          py={1}
          px={2}
        >
          <Text fontSize="xs" color={isStudentView ? 'blue.800' : 'orange.800'} fontWeight="medium">
            {isStudentView ? t('studentViewBanner') : t('previewBanner')}
          </Text>
        </Box>
      )}

      {/* Header */}
      <Box
        as="header"
        position="sticky"
        top={0}
        zIndex={30}
        // Translucent "glass" bar to match the blog header (SiteHeader.astro).
        bg="rgba(255, 255, 255, 0.85)"
        backdropFilter="blur(14px) saturate(1.6)"
        borderBottomWidth="1px"
        borderColor="acp.line"
        h={{ base: '66px', md: '78px' }}
      >
        <Flex
          maxW="960px"
          mx="auto"
          px={4}
          h="100%"
          align="center"
          justify="space-between"
        >
          <Link to={`/${lang}/`} style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }} aria-label={brand.name}>
            <Image
              src={brand.assets.logo}
              alt={brand.name}
              h={{ base: '1.1rem', md: '1.5rem' }}
              w="auto"
              display="block"
            />
          </Link>

          <Flex gap={{ base: 0.5, md: 2 }} align="center" flexShrink={0}>
            {/* Admin/Student view toggle */}
            {isAdmin && (
              <Button
                variant={"acp-ghost" as any}
                size="sm"
                onClick={toggleStudentView}
                aria-label={isStudentView ? t('switchToAdminView') : t('switchToStudentView')}
                title={isStudentView ? t('switchToAdminView') : t('switchToStudentView')}
              >
                {isStudentView ? <EyeOff size={18} /> : <Eye size={18} />}
              </Button>
            )}

            {/* Mobile: globe icon toggles between languages */}
            <Button
              variant={"acp-ghost" as any}
              size="sm"
              display={{ base: 'inline-flex', md: 'none' }}
              onClick={() => {
                const currentLang = lang || i18n.language
                const nextLang = currentLang === 'en' ? 'es' : 'en'
                changeLanguage(nextLang)
              }}
              aria-label={t('language')}
              title={t('language')}
            >
              <Globe size={16} />
            </Button>

            {/* Desktop: globe + select dropdown */}
            <Flex align="center" gap={1} display={{ base: 'none', md: 'flex' }}>
              <Globe size={16} />
              <select
                data-testid="desktop-lang-select"
                value={lang || i18n.language}
                onChange={(e) => changeLanguage(e.target.value)}
                aria-label={t('language')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '14px',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                {SUPPORTED_LANGUAGES.map(code => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </Flex>

            {/* Only an org_admin has a dashboard: a student landed on 403 screens. */}
            {user && adminMembership && (
              <Button
                variant={"acp-ghost" as any}
                size="sm"
                onClick={() => navigate(localePath(`/org/${adminMembership.orgId}/dashboard`))}
                aria-label={t('myOrganization')}
                title={t('myOrganization')}
              >
                <Building2 size={18} />
                <Text ml={1} display={{ base: 'none', md: 'inline' }}>{t('myOrganization')}</Text>
              </Button>
            )}

            {user && (
              <Button variant={"acp-ghost" as any} size="sm" onClick={openProfileModal} aria-label="Profile">
                <User size={18} />
              </Button>
            )}

            {/* Open access: the shell now renders for anonymous visitors too.
                Logged-in users get logout; anonymous get an "Empezar gratis" CTA
                that returns them here after login. */}
            {user ? (
              <Button
                variant={"acp-ghost" as any}
                size="sm"
                onClick={() => { logout(); navigate(`/${lang}/`) }}
              >
                <LogOut />
                <Text ml={1} display={{ base: 'none', md: 'inline' }}>{t('logout')}</Text>
              </Button>
            ) : (
              <Button
                variant={"acp-primary" as any}
                size="sm"
                onClick={() => navigate(`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`)}
              >
                {t('startFree')}
              </Button>
            )}
          </Flex>
        </Flex>
      </Box>

      {/* Content — key forces remount on view toggle so hooks re-fetch with correct headers */}
      <Box key={isStudentView ? 'student' : 'admin'} flex={1} maxW="960px" mx="auto" w="100%" px={{ base: 0, md: 4 }} py={{ base: 2, md: 6 }}>
        <Outlet />
      </Box>

      {/* Footer */}
      <LearnFooter lang={lang || i18n.language} />

      <ProfileEditModal show={isOpen} onClose={closeProfileModal} />
    </Flex>
  )
}
